import React from "react";
import PropTypes from "prop-types";
import { controlPropTypes } from "../formhelpers";
import { processKeyName } from "./utils/keyEvents";

export const DEFAULT_SHORTCUT_KEYS = [ "0", "1", "2", "3", "4", "5", "6", "7", "8", "9" ]; // 0 - 9

const MISSING_VALUE = "_NULL";
export default class Select extends React.Component {
    constructor(props) {
        super(props);
        this.selectRef = null;
        this.onFocus = this.onFocus.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
    }

    convertStringToValue(value) {
        const {addMissing} = this.props;
        if(addMissing && (value === MISSING_VALUE)) {
            return null;
        }
        return value;
    }

    convertValueToString(value) {
        const {addMissing} = this.props;
        if(addMissing && (value === undefined || value === null)) {
            return MISSING_VALUE;
        }
        return value;
    }

    onKeyDown(e) {
        const { descriptor, onKeyDown, onCommitChange, valueKey, labelKey } = this.props;
        const options = this.buildOptions();
        let value = this.getValueFromEvent(e);
        let label = null;
        const key = processKeyName(e.key);
        const index = DEFAULT_SHORTCUT_KEYS.indexOf(key);
        let isKeyboardShortcut = false;
        let doCommit = false;
        if(index !== -1 && index < options.length) {
            // keyboardShortcuts 0-9 commit change.
            // user typed a valid option
            value = options[index][valueKey];
            label = options[index][labelKey];
            isKeyboardShortcut = true;
            doCommit = true;
        }
        onKeyDown && onKeyDown(e, descriptor, this.convertStringToValue(value), isKeyboardShortcut);
        doCommit && onCommitChange && onCommitChange(descriptor, this.convertStringToValue(value), true, label);
    }

    buildKeyboardShortcuts() {
        const { labelKey } = this.props;
        const options = this.buildOptions();
        const keyboardShortcuts = [];
        for(let i = 0; i < 10 && i < options.length ; i++) {
            keyboardShortcuts.push({
                key: DEFAULT_SHORTCUT_KEYS[i],
                name: options[i][labelKey],
            });
        }
        return keyboardShortcuts;
    }

    getValueFromEvent(e) {
        const {multiple = false} = this.props;
        if(multiple) {
            const v = e.target.selectedOptions || [];
            return Array.from(v, it => it.value);
        }
        return e.target.value;
    }

    getLabelFromEvent(e) {
        const {multiple = false, valueKey, labelKey} = this.props;
        const options = this.buildOptions();
        if(multiple) {
            const v = e.target.selectedOptions || [];
            return Array.from(v, it => it.value).map(v => options.find(it => it[valueKey] == v)[labelKey]);
        }
        return options.find(it => it[valueKey] == e.target.value)[labelKey];
    }

    onClick(e) {
        const { descriptor, onClick } = this.props;
        const value = this.getValueFromEvent(e);
        onClick && onClick(e, descriptor, this.convertStringToValue(value));
    }

    onFocus(e) {
        const { descriptor, onFocus } = this.props;
        onFocus && onFocus(e, descriptor, this.convertStringToValue(this.getValueFromEvent(e)), this.buildKeyboardShortcuts());
    }

    onBlur(e) {
        const { descriptor, onBlur } = this.props;
        const value = this.getValueFromEvent(e);
        onBlur && onBlur(e, descriptor, this.convertStringToValue(value));
    }

    onChange(e) {
        const {descriptor, onCommitChange, enabled} = this.props;
        const value = this.getValueFromEvent(e);
        const label = this.getLabelFromEvent(e);
        enabled && onCommitChange && onCommitChange(descriptor, this.convertStringToValue(value), true, label);
    }

    buildOptions() {
        const { options = [], valueKey, labelKey, addMissing } = this.props;
        if(addMissing) {
            const optionsArr = [...options];
            optionsArr.unshift({
                [valueKey]: MISSING_VALUE,
                [labelKey]: "Missing"
            });
            return optionsArr;
        }
        return options;
    }

    render() {
        const { _ref, className, descriptor, placeholder, value, enabled, style, size, valueKey, labelKey, multiple } = this.props;
        const classname = (className || "form-control") + " focusable";
        const selVal = this.convertValueToString(value) || "";
        let selLbl = "";
        const selOpts = this.buildOptions().map((option, index) => {
            const value = option[valueKey];
            const label = option[labelKey];
            if (value === selVal) {
                selLbl = label;
            }
            return (
                <option key={`${index}_${value}`} value={value}>
                    {label}
                </option>
            );
        });
        const select = <select
            ref={(ref) => {
                this.selectRef = ref;
                _ref && _ref(ref);
            }}
            className={classname}
            onChange={this.onChange}
            onClick={this.onClick}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            onKeyDown={this.onKeyDown}
            value={selVal}
            size={size}
            disabled={!enabled}
            style={style}
            multiple={multiple}
        >
            {selOpts}
        </select>;
        // This will emulate a placeholder overlay like an INPUT control, but
        // which SELECT doesn't support for obvious reasons. One more "violate
        // best practices" like ARMUS loves to do and then kludge around!
        if (placeholder && selLbl === "" && descriptor.label) {
            return <div
                style={{
                    display: "block",
                    position: "relative",
                    margin: 0,
                    padding: 0,
                }}
            >
                <div
                    style={{
                        position: "absolute",
                        top: "0.55em",
                        left: "1em",
                        fontSize: "110%",
                        color: "#9d9d9d",
                        pointerEvents: "none",
                    }}
                >
                    {descriptor.label}
                </div>
                {select}
            </div>;
        }
        return select;
    }
}

Select.defaultProps = {
    valueKey: "value",
    labelKey: "label"
};

Select.propTypes = {
    ...controlPropTypes(),
    options: PropTypes.arrayOf(PropTypes.object.isRequired),
    valueKey: PropTypes.string.isRequired,
    labelKey: PropTypes.string.isRequired,
    addMissing: PropTypes.bool
};



// WEBPACK FOOTER //
// ./app/lib/common/descriptorcontrols/BaseControls/Select.js